import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../layouts/Header';
import { Button, Card, Col, Form, Row, Spinner, Table, Badge } from 'react-bootstrap';
import { ArrowLeft } from 'react-bootstrap-icons';
import DatePicker from "react-datepicker";
import { toast } from 'react-toastify';
import axios from '../../api/axios';
import Select from 'react-select';

const JourneyCouponReport = () => {

    const accessToken = localStorage.getItem("accessToken");
    const [skin, setSkin] = useState(localStorage.getItem("skin-mode") || "light");
    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            width: "100%",
            borderRadius: "6px",
            borderColor: state.isFocused ? "#80bdff" : "#ced4da",
            boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : null,
            "&:hover": {
                borderColor: state.isFocused ? "#80bdff" : "#ced4da",
            },
        }),
        placeholder: (provided) => ({
            ...provided,
            color: "#6c757d",
        }),
    };

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const [selectedOutletName, setSelectedOutletName] = useState("");
    const [outlets, setOutlets] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [clicked, setClicked] = useState(false);
    const navigate = useNavigate();
    const [subBtnLoading, setSubBtnLoading] = useState(false);

    const outletOptions = outlets.map((outlet) => ({
        value: outlet._id,
        label: `${outlet.name} (${outlet.city})`,
    }));

    const getOutlets = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get("outlet", { headers });

            if (response?.data?.status === true) {
                const filteredOutlets = response?.data?.data?.outlets.filter((act) => act?.status === "ACTIVE" || "INACTIVE");
                setOutlets(filteredOutlets);
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    const getCouponUsage = async (outlet, date) => {
        if (!selectedOutlet || !startDate) {
            toast.error("Please select both outlet and start date.");
            return;
        }
        
        setSubBtnLoading(true);
        setClicked(true);
        
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            }
            
            // Using the correct API endpoint with query parameters
            const response = await axios.get(`/auto-coupon-allocation/getAutoCouponUsageReport?outlet=${outlet}&startDate=${date.toISOString().split("T")[0]}&endDate=${date.toISOString().split("T")[0]}`, { headers });
            // const response = await axios.get(`/auto-coupon-allocation/getAutoCouponUsageSummary?outlet=${outlet}&date=${date.toISOString().split("T")[0]}&endDate=${date.toISOString().split("T")[0]}`, { headers });
            
            console.log("Response of journey coupon report :", response);
            
            // Handle different response structures
            if (response?.data?.data) {
                setReportData(response.data.data);
                toast.success("Report generated successfully!");
            } else if (response?.data && Array.isArray(response.data)) {
                setReportData(response.data);
                toast.success("Report generated successfully!");
            } else {
                setReportData([]);
                toast.info("No data found for the selected criteria.");
            }
            
        } catch (error) {
            console.log("Error in Fetching journey coupon report :", error);
            handleApiError(error);
            setReportData([]);
        } finally {
            setSubBtnLoading(false);
        }
    }

    const handleApiError = (error) => {
        let errorMessage = 'An error occurred';
        if (error.response?.status === 406) {
            errorMessage = error.response?.data?.error?.message || error.response?.data?.error;
        } else if (error.response?.status === 412) {
            errorMessage = error.response?.data?.data?.message;
        } else if (error.response?.status === 500) {
            errorMessage = error.response?.data?.error;
        }

        toast.error(errorMessage, {
            position: "top-right",
            autoClose: 3000,
            theme: "light",
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        getCouponUsage(selectedOutlet, startDate);
    }

    const handleRefresh = async () => {
        if (selectedOutlet && startDate) {
            getCouponUsage(selectedOutlet, startDate);
        } else {
            toast.error("Please select both outlet and start date to refresh.");
        }
    }

    // Calculate totals for summary
    const calculateTotals = () => {
        const totalAllocated = reportData.reduce((sum, item) => sum + (item.totalAllocatedUsers || 0), 0);
        const totalUsed = reportData.reduce((sum, item) => sum + (item.totalUsedUsers || 0), 0);
        const totalUnused = reportData.reduce((sum, item) => sum + (item.totalUnusedUsers || 0), 0);
        
        return { totalAllocated, totalUsed, totalUnused };
    };

    useEffect(() => {
        const now = new Date();
        const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        const start = new Date(firstOfMonth.setHours(23, 59, 59, 999));
        const end = new Date(currentDate.setHours(23, 59, 59, 999));
        
        setStartDate(start);
        setEndDate(end);
    }, []);

    useEffect(() => {
        getOutlets();
    }, []);

    const { totalAllocated, totalUsed, totalUnused } = calculateTotals();

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className="main main-app p-3 p-lg-4">
                <Card className="shadow-sm border-0">
                    <Card.Header className="bg-white border-0">
                        <div className="card-header bg-white border-0 py-1 d-flex justify-content-between align-items-center">
                            <div>
                                <h4 className="mb-0 fw-bold d-inline-block">
                                    <i className="bi bi-ticket-perforated me-2"></i>JOURNEY COUPON USAGE REPORT
                                </h4>
                            </div>
                            <div>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => navigate('/reports/all-reports')}
                                    className="me-3 d-flex align-items-center fw-semibold"
                                    style={{ height: '38px' }}
                                >
                                    <ArrowLeft className="me-1" /> Back to Reports
                                </Button>
                            </div>
                        </div>
                    </Card.Header>
                    <hr className='mt-0 mb-0' />

                    <Card.Body>
                        {/* Filters Section */}
                        <div className="p-3 bg-light rounded-3 mb-4">
                            <Form onSubmit={handleSubmit}>
                                <Row className="g-3">
                                    <Col md={4} lg={4} sm={3}>
                                        <Form.Group controlId="outletSelect">
                                            <Form.Label className="fw-semibold">
                                                <i className="bi bi-shop me-2"></i>Outlet
                                            </Form.Label>
                                            <Select
                                                options={outletOptions}
                                                value={outletOptions.find(option => option.value === selectedOutlet)}
                                                onChange={(selectedOption) => {
                                                    const selectOutlet = selectedOption?.value;
                                                    setSelectedOutlet(selectOutlet);
                                                    setSelectedOutletName(selectedOption?.label)
                                                }}
                                                isClearable
                                                styles={customStyles}
                                                placeholder="Select Outlet"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={2} lg={2} sm={3}>
                                        <Form.Group controlId="startDate">
                                            <Form.Label className="fw-semibold">
                                                <i className="bi bi-calendar me-2"></i>Start Date
                                            </Form.Label>
                                            <DatePicker
                                                selected={startDate}
                                                onChange={(date) => setStartDate(date)}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="Select Date"
                                                className="form-control border-secondary"
                                            />
                                        </Form.Group>
                                    </Col>
                                    
                                    <Col md={2} lg={2} sm={3}>
                                        <Form.Group controlId="endDate">
                                            <Form.Label className="fw-semibold">
                                                <i className="bi bi-calendar me-2"></i>End Date
                                            </Form.Label>
                                            <DatePicker
                                                selected={endDate}
                                                onChange={(date) => setEndDate(date)}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="Select Date"
                                                className="form-control border-secondary"
                                            />
                                        </Form.Group>
                                    </Col>


                                    <Col md={2} lg={2} className="d-flex align-items-end">
                                        <Button
                                            variant="primary"
                                            className="w-100 py-2"
                                            type="submit"
                                            disabled={subBtnLoading || !selectedOutlet || !startDate}
                                        >
                                            {subBtnLoading ? (
                                                <Spinner size="sm" animation="border" />
                                            ) : (
                                                "Generate Report"
                                            )}
                                        </Button>
                                    </Col>

                                    <Col md={2} lg={2} className="d-flex align-items-end">
                                        <Button
                                            variant="outline-primary"
                                            className="w-100"
                                            onClick={handleRefresh}
                                            disabled={!selectedOutlet || !startDate || subBtnLoading}
                                        >
                                            {subBtnLoading ? (
                                                <Spinner size="sm" animation="border" />
                                            ) : (
                                                "Refresh"
                                            )}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </div>

                        {/* Report Table Section */}
                        {clicked && (
                            <>
                                {reportData && reportData.length > 0 ? (
                                    <>
                                        {/* Summary Cards */}
                                        <Row className="mb-4">
                                            <Col md={4}>
                                                <Card className="bg-primary text-white shadow-sm">
                                                    <Card.Body className="py-3">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <h6 className="mb-0">Total Allocated</h6>
                                                                <h3 className="mb-0 fw-bold">{totalAllocated}</h3>
                                                            </div>
                                                            <i className="bi bi-people fs-1 opacity-50"></i>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                            <Col md={4}>
                                                <Card className="bg-success text-white shadow-sm">
                                                    <Card.Body className="py-3">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <h6 className="mb-0">Total Used</h6>
                                                                <h3 className="mb-0 fw-bold">{totalUsed}</h3>
                                                            </div>
                                                            <i className="bi bi-check-circle fs-1 opacity-50"></i>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                            <Col md={4}>
                                                <Card className="bg-warning text-white shadow-sm">
                                                    <Card.Body className="py-3">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <h6 className="mb-0">Total Unused</h6>
                                                                <h3 className="mb-0 fw-bold">{totalUnused}</h3>
                                                            </div>
                                                            <i className="bi bi-clock-history fs-1 opacity-50"></i>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        </Row>

                                        {/* Table */}
                                        <div className="table-responsive">
                                            <Table striped bordered hover className="align-middle">
                                                <thead className="bg-light">
                                                    <tr>
                                                        <th className="text-center" style={{ width: '60px' }}>#</th>
                                                        <th>Coupon Title</th>
                                                        <th>Usage Type</th>
                                                        <th className="text-center">Total Allocated</th>
                                                        <th className="text-center">Total Used</th>
                                                        <th className="text-center">Total Unused</th>
                                                        <th className="text-center">Usage Rate</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reportData.map((item, index) => {
                                                        const usageRate = item.totalAllocatedUsers > 0 
                                                            ? ((item.totalUsedUsers / item.totalAllocatedUsers) * 100).toFixed(2) 
                                                            : '0.00';
                                                        
                                                        return (
                                                            <tr key={item.autoCouponAllocationId || index}>
                                                                <td className="text-center">{index + 1}</td>
                                                                <td className="fw-semibold text-muted">{item.title || 'N/A'}</td>
                                                                <td>
                                                                    <Badge bg={item.usageType === 'ONE-TIME' ? 'info' : 'secondary'}>
                                                                        {item.usageType || 'N/A'}
                                                                    </Badge>
                                                                </td>
                                                                <td className="text-center fw-bold text-muted">{item.totalAllocatedUsers || 0}</td>
                                                                <td className="text-center text-success fw-bold">{item.totalUsedUsers || 0}</td>
                                                                <td className="text-center text-warning fw-bold">{item.totalUnusedUsers || 0}</td>
                                                                <td className="text-center">
                                                                    <Badge bg={usageRate >= 0.0001 ? 'success' : 'danger'}>
                                                                        {usageRate}%
                                                                    </Badge>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                    {/* Summary Row */}
                                                    <tr className="fw-bold">
                                                        <td colSpan="3" className="text-end">TOTAL:</td>
                                                        <td className="text-center">{totalAllocated}</td>
                                                        <td className="text-center text-success">{totalUsed}</td>
                                                        <td className="text-center text-warning">{totalUnused}</td>
                                                        <td className="text-center">
                                                            <Badge bg={totalAllocated > 0 && (totalUsed / totalAllocated * 100) >= 50 ? 'success' : 'secondary'}>
                                                                {totalAllocated > 0 ? ((totalUsed / totalAllocated) * 100).toFixed(2) : '0.00'}%
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center py-5">
                                        <i className="bi bi-inbox fs-1 text-muted"></i>
                                        <h5 className="mt-3 text-muted">No data available</h5>
                                        <p className="text-muted">Please select outlet and date to generate report</p>
                                    </div>
                                )}
                            </>
                        )}
                    </Card.Body>
                </Card>
            </div>
        </React.Fragment>
    )
}

export default JourneyCouponReport;